import React, { useEffect, useState } from "react";
import api from "../../api";
import Loader from "../../components/Loader";

function Settings() {
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("profile");

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await api.get("/profile");
      setProfile(res.data);
    } catch (err) {
      console.error(err.response || err.message);
      setError("Failed to fetch profile");
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      setLoading(true);
      await api.put("/profile", profile);
      setSuccess("Profile updated successfully ✅");
    } catch (err) {
      console.error(err.response || err.message);
      setError(err.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError("New password and confirm password do not match");
      return;
    }

    try {
      setLoading(true);
      await api.put("/profile", {
        ...profile,
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setSuccess("Password updated successfully 🔒");
    } catch (err) {
      console.error(err.response || err.message);
      setError(err.response?.data?.message || "Password update failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 md:p-8 flex flex-col md:flex-row gap-6 md:gap-8">
      {/* Sidebar */}
      <div className="w-full md:w-64 bg-white shadow-lg rounded-2xl p-5 flex md:flex-col flex-row md:space-y-3 space-y-0 space-x-2 md:space-x-0 border border-gray-100 overflow-x-auto md:overflow-visible">
        <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 md:mb-6 whitespace-nowrap">
          ⚙️ Settings
        </h2>

        <div className="flex md:flex-col gap-2 md:gap-3 min-w-max md:min-w-0">
          {["profile", "system", "security", "password"].map((tab) => (
            <button
              key={tab}
              className={`flex-shrink-0 text-sm md:text-base px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap ${
                activeTab === tab
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "profile"
                ? "👤 Profile Setting"
                : tab === "system"
                ? "💻 System Setting"
                : tab === "security"
                ? "🔐 Security Setting"
                : "🔄 Change Password"}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 space-y-5 md:space-y-6">
        {/* Alerts */}
        {success && (
          <div className="bg-green-50 border border-green-400 text-green-800 px-4 py-3 rounded-xl shadow-sm text-sm md:text-base">
            {success}
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-400 text-red-800 px-4 py-3 rounded-xl shadow-sm text-sm md:text-base">
            {error}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="bg-white shadow-xl rounded-2xl p-5 sm:p-6 md:p-8 border border-gray-100">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-5">
              👤 Profile Settings
            </h2>
            <form onSubmit={updateProfile} className="space-y-4 md:space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  placeholder="Enter your name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  placeholder="Enter your email"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="Enter your phone number"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <button
                type="submit"
                className="w-full sm:w-auto bg-blue-600 text-white font-medium px-6 py-2 rounded-lg hover:bg-blue-700 transition-all shadow-md"
              >
                Update Profile
              </button>
            </form>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === "password" && (
          <div className="bg-white shadow-xl rounded-2xl p-5 sm:p-6 md:p-8 border border-gray-100">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-5">
              🔄 Change Password
            </h2>
            <form onSubmit={updatePassword} className="space-y-4 md:space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                  }
                  placeholder="Enter current password"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                  }
                  placeholder="Enter new password"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                  }
                  placeholder="Re-enter new password"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <button
                type="submit"
                className="w-full sm:w-auto bg-blue-600 text-white font-medium px-6 py-2 rounded-lg hover:bg-blue-700 transition-all shadow-md"
              >
                Change Password
              </button>
            </form>
          </div>
        )}

        {/* Placeholder Tabs */}
        {activeTab === "system" && (
          <div className="bg-white shadow-xl rounded-2xl p-5 sm:p-6 md:p-8 text-gray-600 border border-gray-100">
            <p>💻 System settings content coming soon...</p>
          </div>
        )}
        {activeTab === "security" && (
          <div className="bg-white shadow-xl rounded-2xl p-5 sm:p-6 md:p-8 text-gray-600 border border-gray-100">
            <p>🔐 Security settings content coming soon...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Settings;
